import { createContext, useContext, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CustomQuery, OrdersDrillRequest, PeriodSpec } from '@contracts';
import { useFilters } from '@/lib/filterContext';
import { fetchClients, fetchLayaways, fetchOrders, fetchPayments, fetchProductLines } from '@/services/ordersService';
import { fetchCustomList } from '@/services/customMetricsService';
import { OrdersDrilldownModal } from '@/components/OrdersDrilldownModal';
import { PaymentsDrilldownModal } from '@/components/PaymentsDrilldownModal';
import { ProductsDrilldownModal } from '@/components/ProductsDrilldownModal';
import { ClientsDrilldownModal } from '@/components/ClientsDrilldownModal';
import { LayawaysDrilldownModal } from '@/components/LayawaysDrilldownModal';
import { CustomDrilldownModal } from '@/components/CustomDrilldownModal';

/** Lo que un widget pasa para abrir el drill-down. El período/empresas/scope se
 *  toman de los filtros activos — el front no los recalcula. */
export interface DrillTarget {
  title: string;
  subtitle?: string;
  /** Muestra columna Comisión (drill desde un vendedor). */
  showCommission?: boolean;
  /** KPI desde el que se abrió: el header del modal lidera con esa métrica. */
  metricKind?: 'sales' | 'orders' | 'avgTicket';
  employeeId?: number | null;
  posConfigId?: number | null;
  /** SmartBamboo: vendedor (comisionista) por nombre — SB no expone ids numéricos. */
  sellerName?: string | null;
  /** SmartBamboo: sucursal por nombre (se manda como `sb:branch:<nombre>`). */
  sbBranchName?: string | null;
  /** Override de compañías del drill (Total Ventas por-empresa → solo esa empresa). */
  companies?: string[];
}

interface DrilldownState {
  openOrders: (target: DrillTarget) => void;
  /** Drill-down de PAGOS (widget Transacciones). */
  openPayments: (title: string) => void;
  /** Drill-down de PIEZAS por orden (KPI Productos vendidos). */
  openProducts: (title: string) => void;
  /** Drill-down de Clientes. Odoo: Registrados/Nuevos/Cabina. SB: Altas/Oro/Plata. */
  openClients: (title: string, segment?: ClientSegment) => void;
  /** Drill-down de Apartados. `source` acota la fuente: 'odoo' (VG/Pielé), 'sb'
   *  (SmartBamboo) o sin source = ambas según el filtro. */
  openLayaways: (title: string, source?: 'sb' | 'odoo') => void;
  /** Drill-down GENÉRICO de un widget custom: las filas que componen su número, con
   *  sus mismos filtros Y período (el KPI custom puede tener su propio period). */
  openCustom: (query: CustomQuery, title: string, period?: PeriodSpec) => void;
}

type ClientSegment = 'new' | 'cabin' | 'sbNew' | 'sbOro' | 'sbPlata' | null;

const DrilldownContext = createContext<DrilldownState | null>(null);

/** Un solo modal de drill-down a nivel app; los widgets disparan openOrders/openPayments. */
export function DrilldownProvider({ children }: { children: ReactNode }) {
  const { period, companies, scope } = useFilters();
  const [target, setTarget] = useState<DrillTarget | null>(null);
  const [payTitle, setPayTitle] = useState<string | null>(null);
  const [prodTitle, setProdTitle] = useState<string | null>(null);
  const [cliTitle, setCliTitle] = useState<string | null>(null);
  const [cliSegment, setCliSegment] = useState<ClientSegment>(null);
  const [layTitle, setLayTitle] = useState<string | null>(null);
  const [laySource, setLaySource] = useState<'sb' | 'odoo' | undefined>(undefined);
  const [custom, setCustom] = useState<{ query: CustomQuery; title: string; period?: PeriodSpec } | null>(null);
  const openLayaways = (title: string, source?: 'sb' | 'odoo') => { setLaySource(source); setLayTitle(title); };
  const openClients = (title: string, segment: ClientSegment = null) => {
    setCliSegment(segment);
    setCliTitle(title);
  };
  const openCustom = (query: CustomQuery, title: string, periodOverride?: PeriodSpec) =>
    setCustom({ query, title, period: periodOverride });

  // SB: la sucursal va como `sb:branch:<nombre>` dentro de scope.branches; el vendedor
  // (comisionista) va por nombre en `sellerName`.
  const drillScope = target?.sbBranchName
    ? { ...scope, branches: [...(scope?.branches ?? []), `sb:branch:${target.sbBranchName}`] }
    : scope;
  const req: OrdersDrillRequest | null = target
    ? { period, companies: target.companies ?? companies, scope: drillScope, employeeId: target.employeeId, posConfigId: target.posConfigId, sellerName: target.sellerName }
    : null;
  const payReq: OrdersDrillRequest | null = payTitle !== null ? { period, companies, scope } : null;
  const prodReq: OrdersDrillRequest | null = prodTitle !== null ? { period, companies, scope } : null;
  const cliReq: OrdersDrillRequest | null = cliTitle !== null ? { period, companies, scope, clientSegment: cliSegment } : null;
  const layReq: OrdersDrillRequest | null = layTitle !== null ? { period, companies, scope, source: laySource } : null;

  const { data, isLoading } = useQuery({
    queryKey: ['orders-drill', req],
    queryFn: () => fetchOrders(req as OrdersDrillRequest),
    enabled: req !== null,
  });
  const pay = useQuery({
    queryKey: ['payments-drill', payReq],
    queryFn: () => fetchPayments(payReq as OrdersDrillRequest),
    enabled: payReq !== null,
  });
  const prod = useQuery({
    queryKey: ['products-drill', prodReq],
    queryFn: () => fetchProductLines(prodReq as OrdersDrillRequest),
    enabled: prodReq !== null,
  });
  const cli = useQuery({
    queryKey: ['clients-drill', cliReq],
    queryFn: () => fetchClients(cliReq as OrdersDrillRequest),
    enabled: cliReq !== null,
  });
  const lay = useQuery({
    queryKey: ['layaways-drill', layReq],
    queryFn: () => fetchLayaways(layReq as OrdersDrillRequest),
    enabled: layReq !== null,
  });
  // Si el custom pinnea compañías, el detalle usa ESAS (no el filtro global) — igual que el KPI.
  const custCompanies = custom?.query.companies?.length ? custom.query.companies : companies;
  const cust = useQuery({
    queryKey: ['custom-drill', custom?.query, custom?.period ?? period, custCompanies],
    queryFn: () => fetchCustomList(custom!.query, custom!.period ?? period, custCompanies),
    enabled: custom !== null,
  });

  return (
    <DrilldownContext.Provider value={{ openOrders: setTarget, openPayments: setPayTitle, openProducts: setProdTitle, openClients, openLayaways, openCustom }}>
      {children}
      <OrdersDrilldownModal
        open={target !== null}
        onClose={() => setTarget(null)}
        title={target?.title ?? ''}
        subtitle={target?.subtitle}
        orders={data?.orders ?? []}
        loading={isLoading}
        showCommission={target?.showCommission}
        metricKind={target?.metricKind}
      />
      <PaymentsDrilldownModal
        open={payTitle !== null}
        onClose={() => setPayTitle(null)}
        title={payTitle ?? ''}
        payments={pay.data?.payments ?? []}
        loading={pay.isLoading}
      />
      <ProductsDrilldownModal
        open={prodTitle !== null}
        onClose={() => setProdTitle(null)}
        title={prodTitle ?? ''}
        lines={prod.data?.lines ?? []}
        loading={prod.isLoading}
      />
      <ClientsDrilldownModal
        open={cliTitle !== null}
        onClose={() => setCliTitle(null)}
        title={cliTitle ?? ''}
        clients={cli.data?.clients ?? []}
        loading={cli.isLoading}
      />
      <LayawaysDrilldownModal
        open={layTitle !== null}
        onClose={() => setLayTitle(null)}
        title={layTitle ?? ''}
        layaways={lay.data?.layaways ?? []}
        loading={lay.isLoading}
      />
      <CustomDrilldownModal
        open={custom !== null}
        onClose={() => setCustom(null)}
        title={custom?.title ?? ''}
        columns={cust.data?.columns ?? []}
        rows={cust.data?.rows ?? []}
        loading={cust.isLoading}
        groupBy={custom?.query.detail?.groupBy}
        model={custom?.query.model}
        warning={
          custom?.query.detail?.extraDomain?.length
            ? 'Este detalle aplica filtros adicionales: puede no sumar el total del widget.'
            : undefined
        }
      />
    </DrilldownContext.Provider>
  );
}

export function useDrilldown(): DrilldownState {
  const ctx = useContext(DrilldownContext);
  if (!ctx) throw new Error('useDrilldown debe usarse dentro de DrilldownProvider');
  return ctx;
}
